import PostpaidBucket from './PostpaidBucket.js';
import PrepaidBucket from './PrepaidBucket.js';

export const VIEW_TYPE = {
  DEFAULT: 'default',
  USED: 'used',
  REMAINING: 'remaining',
};

export const VIEW_TYPE_API_VALUE = {
  [VIEW_TYPE.USED]: 'CONSUMED',
  [VIEW_TYPE.REMAINING]: 'BALANCE',
};

/**
 * @property {string} sn - SN related to the consumption data
 * @property {object[]} _data - raw UsageConsumptionSummary response data
 * @property {Bucket[]} _buckets
 */
export default class ConsumptionReport {
  /**
   * @param {string} sn
   * @param {object} consumptionReportData
   * @param {boolean} isPrepaid
   */
  constructor(sn, consumptionReportData, isPrepaid) {
    this.sn = sn;
    this._data = consumptionReportData;
    this._buckets = (this._data.bucket ?? [])
      .map(bucketData =>
        isPrepaid ? new PrepaidBucket(bucketData) : new PostpaidBucket(bucketData),
      )
      .sort(
        isPrepaid ? ConsumptionReport.sortBucketsPrepaid : ConsumptionReport.sortBucketsPostpaid,
      );
  }

  /**
   * Sort postpaid buckets such as that base buckets are first, EU data bucket is last and rest are alphabetically.
   *
   * @param {PostpaidBucket} a
   * @param {PostpaidBucket} b
   * @return {number}
   */
  static sortBucketsPostpaid(a, b) {
    const isFirst = x => x.isBaseBucket;
    const isLast = x => x.isEuData;

    // No swap
    if (isFirst(a) || isLast(b)) {
      return -1;
    }
    // Swap
    if (isLast(a) || isFirst(b)) {
      return 1;
    }

    // Rest alphabetically by name
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  /**
   * Sort prepaid buckets such as that used buckets are last and the rest is descending according to validity.
   *
   * @param {PrepaidBucket} a
   * @param {PrepaidBucket} b
   * @return {number}
   */
  static sortBucketsPrepaid(a, b) {
    const isLast = x => x.remaining === 0;
    // No swap
    if (isLast(b)) {
      return -1;
    }
    // Swap
    if (isLast(a)) {
      return 1;
    }
    return a.validUntilDate - b.validUntilDate;
  }

  /**
   * Search for a bucket matching the predicate function. Expects to find 0 or 1 such bucket, fails otherwise.
   *
   * @param {function(Bucket): boolean} predicate
   * @return {?Bucket}
   */
  getSingleBucketByPredicate(predicate) {
    const buckets = this._buckets.filter(predicate);
    if (buckets.length > 1) {
      throw new Error(
        `No more than one bucket expected, ${buckets.length} buckets found. Filtered by: ${predicate}`,
      );
    }
    if (buckets.length === 0) {
      return null;
    }
    return buckets[0];
  }

  /**
   * Timestamp identifying when the report was generated.
   *
   * @return {Date}
   */
  get effectiveDate() {
    if (!this._data.effectiveDate) {
      throw new Error("Missing 'effectiveDate' property.");
    }
    return new Date(this._data.effectiveDate);
  }

  /**
   * Timestamp identifying when the data portion of the report was generated.
   * As far as we know, this is always the same as `timestamp`, *DO NOT USE* unless
   * a concrete use case is identified.
   *
   * @see effectiveDate
   * @return {Date}
   */
  get effectiveDateForData() {
    if (!this._data.effectiveDate) {
      throw new Error("Missing 'effectiveDateForData' property.");
    }
    return new Date(this._data.effectiveDateForData);
  }

  /**
   * @return {string}
   */
  get consumptionView() {
    const { consumptionView } = this._data;

    if (!consumptionView) {
      return VIEW_TYPE.DEFAULT; // missing field is interpreted as "use default view"
    }

    if (consumptionView === VIEW_TYPE_API_VALUE[VIEW_TYPE.USED]) {
      return VIEW_TYPE.USED;
    }

    if (consumptionView === VIEW_TYPE_API_VALUE[VIEW_TYPE.REMAINING]) {
      return VIEW_TYPE.REMAINING;
    }

    throw new Error(`Unexpected "consumptionView" property value "${consumptionView}"`);
  }

  /**
   * @return {boolean}
   */
  get showRemaining() {
    return this.consumptionView === VIEW_TYPE.REMAINING;
  }

  /**
   * @return {?Bucket}
   */
  get airtimeVoiceBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isVoiceAirtime);
  }

  /**
   * @return {?Bucket}
   */
  get airtimeMessageBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isMessagesAirtime);
  }

  /**
   * @return {?Bucket}
   */
  get airtimeDataBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isDataAirtime);
  }

  /**
   * @return {Bucket[]}
   */
  get airtimeBuckets() {
    return this._buckets.filter(bucket => bucket.isAirtime);
  }

  /**
   * @return {Bucket[]}
   */
  get airtimeOutsidePackagesBuckets() {
    return this._buckets.filter(bucket => bucket.isOutsidePackagesAirtime);
  }

  /**
   * @return {Bucket[]}
   */
  get airtimePackagesBuckets() {
    return this._buckets.filter(bucket => bucket.isPackagesAirtime);
  }

  /**
   * @return {number}
   */
  get airtimePackagesBucketsTotal() {
    return this.airtimePackagesBuckets.reduce((acc, bucket) => acc + bucket.usedEur, 0);
  }

  /**
   * @return {number}
   */
  get airtimeOutsidePackagesBucketsTotal() {
    return this.airtimeOutsidePackagesBuckets.reduce((acc, bucket) => acc + bucket.usedEur, 0);
  }

  /**
   * @return {number}
   */
  get airtimeBucketsTotal() {
    return this.airtimeBuckets.reduce((acc, bucket) => acc + bucket.usedEur, 0);
  }

  /**
   * @return {boolean}
   */
  get hasAirtime() {
    return this.airtimeBuckets.length > 0;
  }

  /**
   * @return {boolean}
   */
  get hasAirtimeOutsidePackages() {
    return this.airtimeOutsidePackagesBuckets.length > 0;
  }
}
